import React, { useEffect, useState, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import logo from "../Assets/Images/Logo.png";
import "./sidebar.css";
import api from "../api";
import { Client } from "@stomp/stompjs";
import SockJS from "sockjs-client";

const Sidebar = () => {
  const [sidebarMode, setSidebarMode] = useState("locked-open");
  const [isOpen, setIsOpen] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();
  const [notifications, setNotifications] = useState();
  const [showNotifications, setShowNotifications] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const roleData = localStorage.getItem("userRole");
  const notificationRef = useRef(null);
  const notificationButtonRef = useRef(null);
  const stompClient = useRef(null);

  useEffect(() => {
    fetchNotifications();
  }, []);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) return;

    // Disable websocket when running locally
    const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1' || process.env.NODE_ENV === 'development';
    if (isLocal) return;

    stompClient.current = new Client({
      webSocketFactory: () => new SockJS("/ws"),
      connectHeaders: {
        Authorization: `Bearer ${token}`,
      },
      reconnectDelay: 5000,
      debug: (str) => console.log("[STOMP]", str),
    });

    stompClient.current.onConnect = () => {
      stompClient.current.subscribe("/user/queue/notifications", (msg) => {
        try {
          if (msg.body) {
            const notification = JSON.parse(msg.body);
            setNotifications((prev) => [notification, ...prev]);
          }
        } catch (e) {
          console.error("Failed to parse notification", e);
        }
      });
    };

    stompClient.current.activate();

    return () => {
      stompClient.current.deactivate();
    };
  }, []);

  const fetchNotifications = async () => {
    try {
      const res = await api.get("/notifications/unread");

      if (res.status === 200) {
        setNotifications(res.data);
      }
    } catch (err) {
      console.error("Error fetching notifications", err);
    }
  };

  const roleBasedMenus = {
    ADMIN: {
      navItems: [
        {
          name: "Anagrafiche Docenti",
          icon: "fa-solid fa-chalkboard-user",
          link: "/docenti",
          title: "Docenti",
          activeMatch: ["/docenti", "/docente/:id"],
        },
        {
          name: "Utenti",
          icon: "fa-solid fa-headset",
          link: "/utenti",
          title: "Utenti",
          activeMatch: ["/utenti", "/utenti/:id", "/aggiungi-utente"],
        },
        {
          name: "Ricerche",
          icon: "bx bxs-file-find",
          link: "/ricerche",
          title: "Ricerche",
          activeMatch: ["/ricerche", "/aggiungi-ricerca", "/ricerca/:id"],
        },
        {
          name: "Sedi",
          icon: "fa-solid fa-map-location",
          link: "/sedi",
          title: "Sedi",
          activeMatch: ["/sedi", "/aggiungi-sede", "/sede/:id"],
        },
        {
          name: "Tipologia Titoli",
          icon: "fas fa-tags",
          link: "/tipologia-titolo",
          title: "Tipologia Titoli",
          activeMatch: [
            "/tipologia-titolo",
            "/aggiungi-tipologia-titolo",
            "/tipologia-titolo/:id",
          ],
        },
        {
          name: "Candidature",
          icon: "fa-solid fa-clipboard-check",
          link: "/candidature",
          title: "Candidature",
          activeMatch: ["/candidature"],
        },
        {
          name: "Mansioni",
          icon: "fa-solid fa-briefcase",
          link: "/mansioni",
          title: "Mansioni",
          activeMatch: ["/mansioni", "/aggiungi-mansione", "/mansioni/:id"],
        },
        {
          name: "Associazioni",
          icon: "fa-solid fa-user-plus",
          link: "/associazioni",
          title: "Associazioni",
          activeMatch: [
            "/associazioni",
            "/aggiungi-associazione",
            "/associazione/:id",
          ],
        },
        {
          name: "Accordo Quadro",
          icon: "fa-solid fa-list-check",
          link: "/accordo-quadro",
          title: "Accordo Quadro",
          activeMatch: [
            "/accordo-quadro",
            "/aggiungi-accordo",
            "/accordo-quadro/:id",
          ],
        },
        {
          name: "Interviste",
          icon: "fa-solid fa-clipboard-question",
          link: "/interviste",
          title: "Interviste",
          activeMatch: [
            "/interviste",
            "/aggiungi-intervista",
            "/intervista/:id",
          ],
        },
        {
          name: "Feedback",
          icon: "fa-solid fa-comments",
          link: "/feedback",
          title: "Feedback",
          activeMatch: ["/feedback", "/aggiungi-feedback", "/feedback/:id"],
        },
        {
          name: "Follow up",
          icon: "fa-solid fa-user-plus",
          link: "/follow-up",
          title: "Follow up",
          activeMatch: ["/follow-up", "/aggiungi-follow-up", "/follow-up/:id"],
        },
        {
          name: "Simulazioni",
          icon: "fa-solid fa-file-circle-question",
          link: "/simulazioni",
          title: "Simulazioni",
          activeMatch: [
            "/simulazioni",
            "/aggiungi-simulazione",
            "/simulazione/:id",
          ],
        },
        {
          name: "Moduli Protocollati",
          icon: "bx bx-spreadsheet",
          link: "/moduli-protocollati",
          title: "Moduli Protocollati",
          activeMatch: [
            "/moduli-protocollati",
            "/moduli-protocollati/:id",
            "/aggiungi-moduli-protocollati",
          ],
        },
      ],
    },

    OPERATOR: {
      navItems: [
        {
          name: "Docenti",
          icon: "fa-solid fa-chalkboard-user",
          link: "/docenti/disponibile",
          title: "Docenti",
          activeMatch: ["/docenti/disponibile", "/docente/disponibile/:id"],
        },
        {
          name: "Ricerche",
          icon: "bx bxs-file-find",
          link: "/ricerche/me",
          title: "Ricerche",
          activeMatch: ["/ricerche/me", "/aggiungi-ricerca", "/ricerca/:id"],
        },
        {
          name: "Sedi",
          icon: "fa-solid fa-location-dot",
          link: "/sedi/me",
          title: "Sedi",
          activeMatch: ["/sedi/me", "/aggiungi-sede", "/sedi/:id"],
        },
        {
          name: "Candidature",
          icon: "fa-solid fa-clipboard-check",
          link: "/candidature",
          title: "Candidature",
          activeMatch: ["/candidature"],
        },
        {
          name: "Feedback",
          icon: "fa-solid fa-comments",
          link: "/aggiungi-feedback",
          title: "Feedback",
          activeMatch: ["/aggiungi-feedback", "/feedback/:id"],
        },
      ],
    },

    TRAINER: {
      navItems: [
        {
          name: "Posizioni Aperte",
          icon: "fa-solid fa-list-check",
          link: "/ricerche/posizioni-aperte",
          title: "Posizioni Aperte",
          activeMatch: ["/ricerche/posizioni-aperte"],
        },
        {
          name: "Mie Candidature",
          icon: "fa-solid fa-briefcase",
          link: "/candidature/me",
          title: "Mie Candidature",
          activeMatch: ["/candidature/me"],
        },

        {
          name: "Titoli di studi",
          icon: "fa-solid fa-certificate",
          link: "/titoli-studi",
          title: "Titoli di Studi",
          activeMatch: [
            "/titoli-studi",
            "/aggiungi-titoli-studi",
            "/titoli-studi/:id",
          ],
        },
        {
          name: "Moduli Protocollati",
          icon: "bx bx-spreadsheet",
          link: "/moduli-protocollati",
          title: "Moduli Protocollati",
          activeMatch: [
            "/moduli-protocollati",
            "/moduli-protocollati/:id",
            "/aggiungi-moduli-protocollati",
          ],
        },
        {
          name: "Curriculum",
          icon: "bx bx-file",
          link: "/curriculum",
          title: "Curriculum",
          activeMatch: ["/curriculum"],
        },
        {
          name: "Esperienze lavorative",
          icon: "fa-solid fa-briefcase",
          link: "/esperienze-lavorative",
          title: "Esperienze lavorative",
          activeMatch: [
            "/esperienze-lavorative",
            "/aggiungi-esperienze-lavorative",
            "/esperienze-lavorative/:id",
          ],
        },
      ],
    },
  };

  const navItems = roleBasedMenus[roleData]?.navItems || [];

  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;

      if (width > 1180) {
        setSidebarMode("locked-open");
        setIsOpen(true);
      } else if (width > 992) {
        setSidebarMode("toggleable");
        setIsOpen(false);
      } else if (width > 880) {
        setSidebarMode("mobile");
        setIsOpen(false);
      }
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleLogout = async () => {
    try {
      const response = await api.post("/auth/logout");
      localStorage.clear();
      navigate("/login");
    } catch (error) {
      console.log(error);
    }
  };

  const toggleSidebar = () => {
    if (sidebarMode === "toggleable" || sidebarMode === "locked-open") {
      setIsOpen(!isOpen);
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      const res = await api.put(`/notifications/${notificationId}/read`);

      if (res.status === 200) {
        setNotifications((prev) => prev.filter((n) => n.id !== notificationId));
      }
    } catch (err) {
      console.error("Error marking notification as read", err);
    }
  };

  const handleNotificationClick = (notification) => {
    if (notification.relatedEntityType === "TRAINER" && notification.relatedEntityId) {
      navigate(`/docente/${notification.relatedEntityId}`);
      markAsRead(notification.id);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        notificationRef.current &&
        !notificationRef.current.contains(event.target) &&
        notificationButtonRef.current &&
        !notificationButtonRef.current.contains(event.target)
      ) {
        setShowNotifications(false);
      }
    };

    if (showNotifications) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showNotifications]);

  const markAllAsRead = async () => {
    try {
      const res = await api.put("/notifications/read-all");
      if (res.status === 200) {
        setNotifications([]);
      }
    } catch (err) {
      console.error("Error marking all notifications as read", err);
    }
  };

  return (
    <>
      <aside
        className={`sidebar ${isOpen ? "open" : "collapsed"} ${
          isMobileMenuOpen ? "mobile-open" : ""
        }`}
      >
        <div className="sidebar-content">
          <div className="logo-container">
            <img src={logo} alt="Logo" className="sidebar-logo" />
          </div>

          <button className="arrow-btn" onClick={toggleSidebar}>
            <i
              className={`fa-solid ${
                isOpen ? "fa-chevron-left" : "fa-chevron-right"
              }`}
            ></i>
          </button>

          <div className="sidebar-menu">
            <div className="sidebar-buttons">
              {navItems.map((item, index) => {
                const isActive = item.activeMatch?.some((route) =>
                  location.pathname.startsWith(route.replace(/:\w+/g, ""))
                );

                return (
                  <button
                    key={index}
                    onClick={() => navigate(item.link)}
                    title={!isOpen ? item.title : ""}
                    className={`sidebar-nav-button ${isActive ? "active" : ""}`}
                  >
                    <i className={item.icon}></i>
                    {isOpen && <span>{item.title}</span>}
                  </button>
                );
              })}
            </div>
            <div className="sidebar-footer">
              <div className="sidebar-subtitle">Settings</div>
              <div className="sidebar-footer-buttons">
                <div className="notification-wrapper">
                  <button
                    ref={notificationButtonRef}
                    // onClick={handleNotificationClick}
                    onClick={() => setShowNotifications((prev) => !prev)}
                    title={!isOpen ? "Notifiche" : ""}
                    className="footer-icon-button notification-btn"
                  >
                    <i className="fa-solid fa-bell" />
                    {isOpen && <span>Notifiche</span>}
                    {notifications && notifications?.length > 0 && (
                      <div className={`badge ${isOpen ? "open" : "collapsed"}`}>
                        {notifications?.length}
                      </div>
                    )}
                  </button>
                </div>

                <button
                  onClick={() => navigate("/profilo")}
                  title={!isOpen ? "Settings" : ""}
                  className={`sidebar-nav-button ${
                    location.pathname.includes("/profilo") ? "active" : ""
                  }`}
                >
                  <i className="fa-solid fa-gear" />
                  {isOpen && <span>Profilo</span>}
                </button>

                <button
                  onClick={handleLogout}
                  title={!isOpen ? "Logout" : ""}
                  className="footer-icon-button"
                >
                  <i className="fa-solid fa-right-from-bracket" />
                  {isOpen && <span>Esci</span>}
                </button>
              </div>
            </div>
          </div>
        </div>
      </aside>

      {/* Notification Popup - Outside Sidebar */}
      {showNotifications && (
        <div ref={notificationRef} className="notification-popup-overlay">
          <div className="notification-popup">
            <div className="notification-header">
              <div className="notification-header-content">
                <i className="fa-solid fa-bell"></i>
                <span>Notifiche</span>
                {notifications && notifications?.length > 0 && (
                  <span className="notification-count">
                    {notifications?.length}
                  </span>
                )}
              </div>
              {notifications && notifications?.length > 0 && (
                <button
                  className="mark-all-read-btn"
                  onClick={markAllAsRead}
                  title="Segna tutte come lette"
                >
                  <i className="fa-solid fa-check-double"></i>
                  <span>Segna tutte come lette</span>
                </button>
              )}
            </div>

            <div className="notification-content">
              {notifications?.length > 0 ? (
                <div className="notification-list">
                  {notifications?.map((n) => (
                    <div
                      key={n.id}
                      className={`notification-item unread ${
                        n.relatedEntityType === "TRAINER" && n.relatedEntityId
                          ? "clickable"
                          : ""
                      }`}
                      onClick={() => {
                        if (
                          n.relatedEntityType === "TRAINER" &&
                          n.relatedEntityId
                        ) {
                          handleNotificationClick(n);
                        }
                      }}
                    >
                      <div className="notification-body">
                        <div className="notification-message">
                          {n.message || "Hai una nuova notifica"}
                        </div>
                        <div className="notification-time">
                          {new Date(n.timestamp).toLocaleString()}
                        </div>
                      </div>
                      <div className="notification-actions">
                        <button
                          className="mark-read-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            markAsRead(n.id);
                          }}
                          title="Segna come letta"
                        >
                          <i className="fa-solid fa-envelope-open-text"></i>{" "}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="no-notifications">
                  <i className="fa-solid fa-bell-slash"></i>
                  <span>Nessuna notifica</span>
                  <p>Non hai notifiche non lette</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Sidebar;
